<!-- Generated by Edumotion tools/migration-entry/sync.mjs. Edit the shared template. -->
<script setup>
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import eduLogo from './edumotion-logo.png'
function getPortalDomain() {
  try {
    // Имя переменной намеренно другое: сборка «Обучения сотрудников» запрещает
    // в ассетах короткое имя из двух букв (проверка на inline-фикстуру авторизации).
    const portalApi = window.BX24 || window.B24
    return portalApi?.getDomain?.() || portalApi?.getAuth?.()?.domain || null
  } catch (error) {
    console.warn('Не удалось определить портал для перехода в Edumotion.', error)
    return null
  }
}

/**
 * Предложение перейти в Edumotion из прежнего учебного приложения.
 *
 * Дизайн общий с полноэкранным промо в виджете-хабе (b24-statistics,
 * `EdumotionPromoFull.vue`): тёмный холст с авророй, слева питч и одна кнопка,
 * справа витрина. Хаб показывает его всей фабрике с общим текстом, здесь тот же
 * экран говорит про конкретное приложение, из которого пришёл человек: курсам
 * не хватает проверки знаний и сроков, тестам не хватает обучения ДО теста,
 * базе знаний не хватает ассистента, LMS24 не хватает ИИ и отчётов.
 *
 * Экран открывается сам один раз. Дальше на панели остаётся тихая полоса,
 * которая открывает тот же экран заново.
 *
 * Тихие первые сутки и закрытие на 14 дней (решение 24.09.2026), правило общее с
 * баннером фабрики `_global-lib/edumotion-offer.js` и промо хаба. Человек, который
 * только открыл приложение, пришёл за ним, а не за Edumotion, и экран поверх первого
 * же открытия читается как навязывание. Поэтому ни экрана, ни полосы, пока с первого
 * открытия в этом браузере не прошли сутки; открытие после паузы от 14 дней считается
 * новым первым. Отметки лежат в localStorage по источнику (`edumotion:promo:visits:`);
 * если хранилище недоступно, первое открытие не отличить от сотого, и промо не
 * показывается. Крестик экрана и крестик полосы прячут оба на 14 дней. До сети дело в
 * тихие сутки не доходит: рубильник не спрашиваем.
 *
 * Рубильник живёт на стороне Edumotion (`/public/migration/portal` →
 * `promoEnabled`): приложений семь, они в чужих репозиториях, и включение показа
 * не должно стоить семи сборок. Пока ответа нет, не показывается ничего:
 * молчание сети и выключенный показ для человека одно и то же.
 *
 * Факты сверены с кодом: бесплатный тариф на десять обучающихся без срока и 14
 * дней пробного (`@lms/pricing`), семь типов вопросов (`core/quiz`), сборка
 * курса из PDF и DOCX (`core/course-import`), тренажёры (`core/dialogue-ai`),
 * ассистент базы знаний (`core/kb`), оценка 360 (`core/assessment-360`).
 * Вебинаров в витрине нет намеренно: фича закрыта гейтом и клиентам не открыта.
 */
const SEEN_KEY = 'edu:migration-offer:seen'
const CARD_SEEN_KEY = 'edu:migration-offer:card-seen'
const APP_CODE = 'aventra.edumotion_lms'
const API_BASE = 'https://api.edumotion.ru'
const DEMO_BASE = 'https://org.edumotion.ru/demo/start'
const PUBLIC_CARD = 'https://www.bitrix24.ru/apps/app/' + APP_CODE + '/'
const VISITS_KEY = 'edumotion:promo:visits:learning'
const DISMISS_KEY = 'edumotion:promo:dismissedAt'
const QUIET_MS = 24 * 3600 * 1000
const PAUSE_MS = 14 * 24 * 3600 * 1000
const HIDE_MS = 14 * 24 * 3600 * 1000

const ICON_PATHS = {
  book: '<path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H19v15H6.5A2.5 2.5 0 0 0 4 20.5z"/><path d="M4 20.5A2.5 2.5 0 0 1 6.5 18H19v3H6.5"/>',
  check: '<circle cx="12" cy="12" r="9"/><path d="M8.5 12l2.5 2.5 4.5-5"/>',
  chat: '<path d="M20 13a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z"/>',
  spark: '<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"/><path d="M18.5 16.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z"/>',
  file: '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="M9 13h6M9 17h4"/>',
  chart: '<path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/>',
  route: '<circle cx="6" cy="19" r="2.5"/><circle cx="18" cy="5" r="2.5"/><path d="M8.5 19H14a4 4 0 0 0 0-8h-4a4 4 0 0 1 0-8h5.5"/>',
  box: '<path d="M4 8V6a2 2 0 0 1 2-2h5l2 2h5a2 2 0 0 1 2 2v1"/><path d="M3 11h18l-1.6 8.2A2 2 0 0 1 17.4 21H6.6a2 2 0 0 1-2-1.8z"/><path d="M12 14v4M10 16l2 2 2-2"/>',
}
function iconSvg(key, size = 18) {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${ICON_PATHS[key]}</svg>`
}

/** Витрина одна на всех: она про Edumotion, а не про приложение-источник. */
const TILES = [
  { icon: 'book', title: 'Курсы и траектории', desc: 'Видео, документы, презентации, маршруты со сроками', hue: '#60a5fa' },
  { icon: 'check', title: 'Проверка знаний', desc: 'Семь типов вопросов, банк, практические задания', hue: '#a78bfa' },
  { icon: 'file', title: 'Курс из документа', desc: 'PDF или DOCX превращается в уроки и вопросы', hue: '#22d3ee' },
  { icon: 'spark', title: 'База знаний с ассистентом', desc: 'Ответы по документам компании со ссылкой на источник', hue: '#f472b6' },
  { icon: 'chat', title: 'Диалоговые тренажёры', desc: 'Отработка сложного разговора с разбором от ИИ', hue: '#fbbf24' },
  { icon: 'chart', title: 'Аналитика и сертификаты', desc: 'Завершаемость, просрочки, именные сертификаты', hue: '#34d399' },
]

const SOURCES = {
  learning: {
    head: 'Курсы у вас есть.',
    accent: 'В Edumotion к ним приходит результат',
    lead: 'То же обучение внутри Битрикс24, только видно, кто прошёл, кто застрял и кто просрочил. Сотрудники, отделы и должности приезжают из портала сами.',
    materials: 'курсы и тесты',
    chips: ['Тесты', 'Сроки', 'Отчёты'],
    glyph: 'check',
  },
  testing: {
    head: 'Тест показывает провал.',
    accent: 'Edumotion показывает, почему',
    lead: 'В Edumotion перед тестом стоит обучение: сотрудник читает, смотрит и тренируется, а вы видите весь путь, а не только оценку.',
    materials: 'тесты',
    chips: ['Курсы', 'Пересдачи', 'Сертификаты'],
    glyph: 'book',
  },
  knowledge: {
    head: 'Статьи читают.',
    accent: 'В Edumotion видно, что поняли',
    lead: 'Те же разделы и статьи, плюс ассистент, который отвечает по вашим документам, и проверка знаний по прочитанному.',
    materials: 'статьи и тесты',
    chips: ['Ассистент', 'Курсы', 'Отчёты'],
    glyph: 'spark',
  },
  lms24: {
    head: 'Курсы, тесты и статьи у вас есть.',
    accent: 'Дальше идут ИИ и отчёты',
    lead: 'Edumotion берёт то же самое и добавляет сборку курса из документа, тренажёры разговора и аналитику по каждому сотруднику.',
    materials: 'курсы, тесты и статьи',
    chips: ['ИИ', 'Тренажёры', 'Отчёты'],
    glyph: 'file',
  },
}
const S = SOURCES['learning'] || SOURCES.learning
const SECTION = { name: 'Возможности', chips: S.chips, glyph: S.glyph, tiles: TILES, from: '#5eb0ff', to: '#8b7bff' }

const portalDomain = computed(() => {
  const raw = getPortalDomain()
  // Auth supplies the host; reject paths, credentials and protocols in a host.
  return typeof raw === 'string' && /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9-]+(?::\d{1,5})?$/i.test(raw)
    ? raw : null
})

const installed = ref(false)
const appPath = ref('')
const enabled = ref(false)
const modalOpen = ref(false)
const isExiting = ref(false)

/**
 * Путь внутри портала: карточка приложения в Маркете живёт по
 * `/marketplace/detail/<код>/`, а установленное приложение по пути, который
 * отдаёт ручка. Именно путь, а не полный адрес: по нему открываем через BX24,
 * не покидая портал.
 */
const portalPath = computed(() =>
  installed.value
    ? appPath.value || '/marketplace/'
    : '/marketplace/detail/' + APP_CODE + '/'
)

/**
 * Запасной адрес на случай, когда BX24 недоступен (приложение открыто не в
 * портале). Новая вкладка здесь именно запасной путь: в десктопном клиенте
 * Битрикс24 она уходит в системный браузер, где сессии портала нет, и человек
 * видит форму входа вместо карточки.
 */
const primaryHref = computed(() => {
  if (!portalDomain.value) return PUBLIC_CARD
  return 'https://' + portalDomain.value + portalPath.value
})
const primaryLabel = computed(() =>
  installed.value ? 'Открыть Edumotion' : 'Установить и попробовать'
)

const demoHref = computed(() => {
  const query = new URLSearchParams({ from: 'learning' })
  if (portalDomain.value) query.set('portal', portalDomain.value)
  return DEMO_BASE + '?' + query.toString()
})

/** Киллер-фича: переносить руками ничего не нужно. */
const move = computed(() =>
  installed.value
    ? 'Edumotion уже стоит на вашем портале. Откройте его и запустите перенос: ваши материалы переедут за час.'
    : 'Перенесём все ваши материалы бесплатно, за час. Делать вам ничего не нужно.'
)

// Свет за курсором едет одной строкой стиля на корне.
const pointer = reactive({ mx: '72%', my: '10%' })
const rootStyle = computed(() => ({
  '--e2-from': SECTION.from,
  '--e2-to': SECTION.to,
  '--mx': pointer.mx,
  '--my': pointer.my,
}))

// Искры генерируем один раз: позиция, размер и тайминг вразнобой.
const sparks = Array.from({ length: 16 }, () => ({
  left: Math.round(Math.random() * 100),
  size: (2 + Math.random() * 2.4).toFixed(1),
  dur: (7 + Math.random() * 8).toFixed(1),
  delay: (Math.random() * 9).toFixed(1),
}))

function onPointer(e) {
  const r = (e.currentTarget).getBoundingClientRect()
  if (!r.width || !r.height) return
  pointer.mx = (((e.clientX - r.left) / r.width) * 100).toFixed(1) + '%'
  pointer.my = (((e.clientY - r.top) / r.height) * 100).toFixed(1) + '%'
}

// 3D-наклон плитки к курсору. На mouseleave очищаем инлайн-трансформ.
function onTileMove(e) {
  const el = e.currentTarget
  const r = el.getBoundingClientRect()
  const x = (e.clientX - r.left) / r.width - 0.5
  const y = (e.clientY - r.top) / r.height - 0.5
  el.style.transform = `translateY(-4px) rotateX(${(-y * 7).toFixed(2)}deg) rotateY(${(x * 9).toFixed(2)}deg)`
}
function onTileLeave(e) {
  const el = e.currentTarget
  el.style.transform = ''
}

/**
 * След промо уходит в Edumotion: показ, клик по установке, клик по демо,
 * закрытие и возврат из полосы. Параметры идут в адресе, тела нет: так браузер
 * шлёт простой запрос без preflight. Ошибки гасим, мы гости в чужом приложении.
 */
function track(event, placement) {
  const query = new URLSearchParams({
    source: 'learning',
    event,
    placement,
    installed: installed.value ? 'true' : 'false',
  })
  if (portalDomain.value) query.set('domain', portalDomain.value)
  const url = API_BASE + '/public/migration/event?' + query.toString()
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url)) return
  } catch (error) {
    // Ниже обычный запрос.
  }
  try {
    fetch(url, { method: 'POST', keepalive: true }).catch(() => {})
  } catch (error) {
    /* счётчик молчит */
  }
}

function placement() {
  return modalOpen.value ? 'modal' : 'card'
}

/** Показ полосы считаем раз в сутки: она висит на панели всё время. */
function cardShownToday() {
  const today = new Date().toISOString().slice(0, 10)
  try {
    if (localStorage.getItem(CARD_SEEN_KEY) === today) return true
    localStorage.setItem(CARD_SEEN_KEY, today)
  } catch (error) {
    // Приватное окно: тогда показ посчитается ещё раз, это лучше падения.
  }
  return false
}

function remember() {
  try {
    localStorage.setItem(SEEN_KEY, String(Date.now()))
  } catch (error) {
    // Приватное окно: тогда предложение придёт ещё раз, и это лучше, чем падение.
  }
}

/**
 * Отмечает открытие и отвечает, можно ли показывать промо в этот раз. Запись
 * `{first, last}` в миллисекундах. Нет записи, она битая или время в ней из будущего,
 * либо с прошлого открытия прошло 14 дней и больше: это первое открытие, сутки идут с
 * него. Хранилище, которое бросает исключение или молча не сохраняет отметку, считается
 * недоступным.
 */
function visitAllowsPromo(now) {
  try {
    let visits = null
    try {
      visits = JSON.parse(localStorage.getItem(VISITS_KEY) || 'null')
    } catch (error) {
      visits = null
    }
    const first = visits ? Number(visits.first) : NaN
    const last = visits ? Number(visits.last) : NaN
    const known = first > 0 && first <= last && last <= now
    const fresh = !known || now - last >= PAUSE_MS
    const next = JSON.stringify({ first: fresh ? now : first, last: now })
    localStorage.setItem(VISITS_KEY, next)
    if (localStorage.getItem(VISITS_KEY) !== next) return false
    return !fresh && now - first >= QUIET_MS
  } catch (error) {
    return false
  }
}

/** Закрыто меньше 14 дней назад. Нечитаемое хранилище тоже «закрыто»: не показываем. */
function dismissedRecently(now) {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0)
    return at > 0 && now - at < HIDE_MS
  } catch (error) {
    return true
  }
}

function dismiss() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
  } catch (error) {
    // Без хранилища промо и так не показывается.
  }
}

/** Крестик экрана: и экран, и полоса уходят на 14 дней. */
function close() {
  if (isExiting.value) return
  isExiting.value = true
  track('dismissed', 'modal')
  remember()
  dismiss()
  setTimeout(() => {
    modalOpen.value = false
    isExiting.value = false
    enabled.value = false
  }, 220)
}

/** Экран уходит, полоса остаётся: человек пошёл ставить Edumotion, это не отказ. */
function collapse() {
  if (isExiting.value) return
  isExiting.value = true
  setTimeout(() => {
    modalOpen.value = false
    isExiting.value = false
  }, 220)
}

/** Крестик полосы: промо прячется на 14 дней, как после закрытия экрана. */
function hideStrip() {
  track('dismissed', 'card')
  dismiss()
  enabled.value = false
}

/** Полоса открывает экран заново: точка возврата к разговору. */
function reopen() {
  modalOpen.value = true
  track('reopened', 'card')
}

function onInstall(e) {
  track('install_click', placement())
  remember()
  // Внутри портала уводим средствами Битрикса: слайдер открывается поверх
  // текущей страницы, сессия сохраняется, новая вкладка не нужна.
  const api = (window).BX24
  if (api && typeof api.openPath === 'function') {
    e.preventDefault()
    collapse()
    api.openPath(portalPath.value, (res) => {
      if (res && res.result === 'error') window.open(primaryHref.value, '_blank', 'noopener')
    })
  }
}

function onDemo() {
  track('demo_click', placement())
  remember()
}

function onKey(e) {
  if (e.key === 'Escape' && modalOpen.value) close()
}

onMounted(() => {
  let seen = ''
  try {
    seen = localStorage.getItem(SEEN_KEY) || ''
  } catch (error) {
    seen = ''
  }
  document.addEventListener('keydown', onKey)

  // Тихие сутки и закрытие: до сети не доходим, рубильник не спрашиваем.
  const now = Date.now()
  const visitAllows = visitAllowsPromo(now)
  if (!visitAllows || dismissedRecently(now)) return

  // Стоит ли Edumotion на этом портале и включён ли показ. Домена может не быть
  // вовсе, тогда спрашиваем рубильник без него.
  const query = portalDomain.value ? '?domain=' + encodeURIComponent(portalDomain.value) : ''
  fetch(API_BASE + '/public/migration/portal' + query)
    .then((r) => (r.ok ? r.json() : null))
    .then((body) => {
      const data = body && body.data ? body.data : null
      if (!data || !data.promoEnabled) return
      installed.value = Boolean(data.installed)
      appPath.value = data.appPath || ''
      enabled.value = true
      if (!seen) modalOpen.value = true
      if (modalOpen.value || !cardShownToday()) track('shown', placement())
    })
    .catch(() => {
      // Сеть молчит: тогда не показываем ничего.
    })
})

onUnmounted(() => document.removeEventListener('keydown', onKey))
</script>

<template>
  <!-- На панели остаётся тихая полоса: полноэкранное промо человек видит один
       раз, и без этой строки вернуться к предложению ему будет некуда. -->
  <div v-if="enabled && !modalOpen" class="e2-strip" data-testid="edumotion-migration-entry">
    <span class="e2-strip-mark" aria-hidden="true">
      <img :src="eduLogo" alt="" width="26" height="26">
    </span>
    <span class="e2-strip-txt">
      <b>Мы переосмыслили обучение в Битрикс24</b>
      <i>Попробуйте бесплатно. Все ваши материалы перенесём за час.</i>
    </span>
    <button type="button" class="e2-strip-cta" @click="reopen">Попробовать</button>
    <button type="button" class="e2-strip-close" aria-label="Скрыть на 14 дней" title="Скрыть на 14 дней" @click="hideStrip">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
    </button>
  </div>

  <div
    v-if="enabled && modalOpen"
    class="e2-root"
    :class="{ 'e2-root--exiting': isExiting }"
    :style="rootStyle"
    role="dialog"
    aria-modal="true"
    aria-labelledby="e2-title"
    data-testid="edumotion-migration-modal"
    @mousemove="onPointer"
  >
    <div class="e2-bg" aria-hidden="true" />
    <div class="e2-glow e2-glow--a" aria-hidden="true" />
    <div class="e2-glow e2-glow--b" aria-hidden="true" />
    <div class="e2-glow e2-glow--c" aria-hidden="true" />
    <div class="e2-sparks" aria-hidden="true">
      <span
        v-for="(sp, i) in sparks"
        :key="i"
        class="e2-spark"
        :style="{ left: sp.left + '%', width: sp.size + 'px', height: sp.size + 'px', animationDuration: sp.dur + 's', animationDelay: sp.delay + 's' }"
      />
    </div>
    <div class="e2-vig" aria-hidden="true" />
    <div class="e2-spot" aria-hidden="true" />

    <button type="button" class="e2-close" aria-label="Закрыть" @click="close">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
    </button>

    <div class="e2-scroll">
      <div class="e2-grid">

        <!-- Левая половина неизменна: предложение и кнопка всё время перед глазами. -->
        <section class="e2-pitch">
          <div class="e2-brand">
            <img :src="eduLogo" alt="Edumotion" width="44" height="44">
            <span class="e2-eyebrow">Edumotion<br><b>флагман Aventra</b></span>
          </div>

          <h1 id="e2-title" class="e2-title">
            {{ S.head }} <span class="e2-accent">{{ S.accent }}</span>
          </h1>

          <p class="e2-lead">{{ S.lead }}</p>

          <p class="e2-move">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 8V6a2 2 0 0 1 2-2h5l2 2h5a2 2 0 0 1 2 2v1" /><path d="M3 11h18l-1.6 8.2A2 2 0 0 1 17.4 21H6.6a2 2 0 0 1-2-1.8z" /><path d="M12 14v4M10 16l2 2 2-2" /></svg>
            <span>{{ move }}</span>
          </p>

          <div class="e2-actions">
            <a class="e2-cta" :href="primaryHref" target="_blank" rel="noopener noreferrer" @click="onInstall">
              {{ primaryLabel }}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h13M12 6l6 6-6 6" /></svg>
            </a>
            <a class="e2-demo" :href="demoHref" target="_blank" rel="noopener noreferrer" @click="onDemo">Посмотреть без установки</a>
          </div>

        </section>

        <!-- Правая половина - витрина: одна hero-карточка и плитки, без листания. -->
        <section class="e2-panel">
          <div class="e2-hero">
            <div class="e2-hero-mesh" aria-hidden="true" />
            <div class="e2-hero-ring" aria-hidden="true" />
            <span class="e2-hero-glyph" aria-hidden="true" v-html="iconSvg(SECTION.glyph, 140)" />
            <div class="e2-hero-body">
              <span class="e2-hero-kick">Edumotion</span>
              <span class="e2-hero-name">{{ SECTION.name }}</span>
              <div class="e2-hero-chips">
                <span v-for="c in SECTION.chips" :key="c" class="e2-chip">{{ c }}</span>
              </div>
            </div>
          </div>

          <ul class="e2-tiles e2-tiles--two">
            <li
              v-for="(t, i) in SECTION.tiles"
              :key="t.title"
              class="e2-tile"
              :style="{ '--hue': t.hue, '--d': i * 55 + 'ms' }"
              @mousemove="onTileMove"
              @mouseleave="onTileLeave"
            >
              <span class="e2-tile-ic" aria-hidden="true" v-html="iconSvg(t.icon)" />
              <span class="e2-tile-txt">
                <span class="e2-tile-t">{{ t.title }}</span>
                <span class="e2-tile-d">{{ t.desc }}</span>
              </span>
            </li>
          </ul>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.e2-root { position: fixed; inset: 0; z-index: 2147483646; overflow: hidden; color: #eef2f8; font-family: var(--w-font, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif); animation: e2-in 0.4s cubic-bezier(.2,.8,.3,1) both; --e2-from: #5eb0ff; --e2-to: #8b7bff; --edu-a: #7c5cff; --edu-b: #4f8bff; --mx: 72%; --my: 10%; }
.e2-root--exiting { animation: e2-out 0.22s ease both; }
@keyframes e2-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes e2-out { from { opacity: 1; } to { opacity: 0; } }

.e2-bg { position: absolute; inset: 0; background: linear-gradient(150deg, #0e1c38 0%, #0a1330 46%, #070c1c 100%); }
/* Живая аврора: пятна дрейфуют и дышат, красятся цветом раздела и перетекают -
   смена цвета сама сообщает о переключении раньше анимации содержимого. */
.e2-glow { position: absolute; border-radius: 50%; filter: blur(60px); transition: background 0.6s ease; }
.e2-glow--a { top: -16%; left: -8%; width: 56%; height: 66%; background: radial-gradient(circle, var(--e2-from), transparent 66%); opacity: 0.42; animation: e2-drift-a 18s ease-in-out infinite; }
.e2-glow--b { bottom: -20%; right: -6%; width: 54%; height: 64%; background: radial-gradient(circle, var(--e2-to), transparent 64%); opacity: 0.34; animation: e2-drift-b 22s ease-in-out infinite; }
.e2-glow--c { top: 30%; left: 38%; width: 40%; height: 44%; background: radial-gradient(circle, var(--e2-to), transparent 68%); opacity: 0.16; animation: e2-drift-a 26s ease-in-out infinite reverse; }

.e2-sparks { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 1; }
.e2-spark { position: absolute; bottom: -8px; border-radius: 50%; background: #cfe0ff; opacity: 0; filter: drop-shadow(0 0 6px rgba(150,190,255,0.9)); animation: e2-float linear infinite; }

.e2-vig { position: absolute; inset: 0; pointer-events: none; z-index: 2; background: radial-gradient(120% 80% at 50% 40%, transparent 55%, rgba(3,7,18,0.55)); }
.e2-spot { position: absolute; inset: 0; pointer-events: none; z-index: 3; mix-blend-mode: screen; background: radial-gradient(280px circle at var(--mx) var(--my), rgba(150,190,255,0.14), transparent 60%); }

.e2-close { position: absolute; top: 16px; right: 18px; width: 44px; height: 44px; border-radius: 12px; border: none; background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.6); display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 6; transition: background 0.18s, color 0.18s; }
.e2-close:hover { background: rgba(255,255,255,0.13); color: #fff; }
.e2-close:focus-visible { outline: 2px solid #cfe0ff; outline-offset: 2px; }

.e2-scroll { position: relative; z-index: 4; height: 100%; overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; display: flex; }

.e2-grid { margin: auto; width: 100%; max-width: 1240px; padding: 48px clamp(24px, 5vw, 72px); display: grid; grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr); gap: clamp(32px, 5vw, 64px); align-items: center; }

.e2-pitch { text-align: left; }
.e2-pitch > * { animation: e2-rise 0.62s cubic-bezier(.2,.85,.3,1) both; }
.e2-brand { display: flex; align-items: center; gap: 12px; animation-delay: 0.05s; }
.e2-brand img { border-radius: 12px; box-shadow: 0 8px 22px rgba(91,110,255,0.4); animation: e2-logo 5s ease-in-out infinite; }
.e2-eyebrow { font-size: 11.5px; line-height: 1.35; letter-spacing: 0.09em; text-transform: uppercase; color: #7f92b3; }
.e2-eyebrow b { font-weight: 700; background: linear-gradient(92deg, var(--e2-from), var(--e2-to)); -webkit-background-clip: text; background-clip: text; color: transparent; transition: background 0.6s ease; }

.e2-title { margin: 26px 0 0; font-size: clamp(30px, 3.5vw, 47px); line-height: 1.09; font-weight: 700; letter-spacing: -0.03em; animation: e2-titlein 0.8s cubic-bezier(.2,.85,.3,1) both; animation-delay: 0.12s; }
.e2-accent { background: linear-gradient(96deg, var(--e2-from), var(--e2-to), var(--e2-from)); background-size: 220% auto; -webkit-background-clip: text; background-clip: text; color: transparent; animation: e2-shimmer 6.5s linear infinite; }
.e2-lead { margin: 18px 0 0; font-size: 15.5px; line-height: 1.6; color: #a3b3d0; max-width: 46ch; animation-delay: 0.24s; }

.e2-move { position: relative; overflow: hidden; margin: 20px 0 0; display: flex; align-items: flex-start; gap: 11px; padding: 13px 16px; border-radius: 14px; background: rgba(94, 176, 255, 0.1); background: color-mix(in srgb, var(--e2-from) 10%, rgba(255,255,255,0.02)); border: 1px solid rgba(94, 176, 255, 0.24); border: 1px solid color-mix(in srgb, var(--e2-from) 24%, transparent); font-size: 13.5px; line-height: 1.5; color: #dbe6f7; max-width: 46ch; transition: background 0.6s ease, border-color 0.6s ease; animation-delay: 0.33s; }
.e2-move::after { content: ''; position: absolute; top: 0; left: 0; width: 45%; height: 100%; background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--e2-from) 22%, transparent), transparent); transform: translateX(-160%) skewX(-16deg); animation: e2-sheen 7s ease-in-out 2s infinite; }
.e2-move > svg { color: var(--e2-from); flex: none; margin-top: 2px; transition: color 0.6s ease; animation: e2-bob 3.4s ease-in-out infinite; }
.e2-move b { color: #fff; font-weight: 650; }

.e2-actions { margin-top: 26px; display: flex; align-items: center; gap: 22px; flex-wrap: wrap; animation-delay: 0.42s; }
.e2-cta { position: relative; display: inline-flex; align-items: center; gap: 10px; padding: 16px 32px; border-radius: 999px; background: linear-gradient(96deg, var(--edu-a), var(--edu-b)); color: #fff; font-size: 16px; font-weight: 650; text-decoration: none; cursor: pointer; overflow: hidden; transition: transform 0.16s; }
.e2-cta::before { content: ''; position: absolute; inset: -3px; border-radius: inherit; background: linear-gradient(96deg, var(--edu-a), var(--edu-b)); filter: blur(15px); opacity: 0.5; z-index: -1; animation: e2-halo 3s ease-in-out infinite; }
.e2-cta::after { content: ''; position: absolute; top: 0; left: 0; width: 38%; height: 100%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent); transform: translateX(-160%) skewX(-18deg); animation: e2-shine 4.8s ease-in-out 1.4s infinite; }
.e2-cta:hover { transform: translateY(-2px); }
.e2-cta:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
.e2-cta > svg { transition: transform 0.18s; }
.e2-cta:hover > svg { transform: translateX(4px); }
.e2-demo { display: inline-flex; align-items: center; min-height: 44px; color: #aeb9d4; font-size: 14.5px; text-decoration: underline; text-decoration-color: rgba(174,185,212,0.35); text-underline-offset: 4px; transition: color 0.16s, text-decoration-color 0.16s; }
.e2-demo:hover { color: #fff; border-bottom-color: rgba(255,255,255,0.6); }

.e2-trust { margin: 22px 0 0; font-size: 12px; color: #67769a; animation-delay: 0.52s; }
.e2-skip { min-height: 44px; margin-top: 6px; background: none; border: none; padding: 0; font: inherit; font-size: 13.5px; color: #67769a; cursor: pointer; animation-delay: 0.58s; }
.e2-skip:hover { color: #c3cfe4; }

.e2-panel { position: relative; border-radius: 22px; padding: 20px; background: rgba(255,255,255,0.03); border: 1px solid rgba(94, 176, 255, 0.2); border: 1px solid color-mix(in srgb, var(--e2-from) 20%, rgba(255,255,255,0.06)); backdrop-filter: blur(10px); transition: border-color 0.6s ease; animation: e2-panelin 0.75s cubic-bezier(.2,.85,.3,1) 0.34s both; box-shadow: 0 24px 70px rgba(4,10,28,0.5); }
.e2-panel-head { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 0 2px 14px; }
.e2-panel-count { font-size: 11.5px; letter-spacing: 0.06em; text-transform: uppercase; color: #67769a; }
.e2-nav { display: flex; align-items: center; gap: 8px; }
.e2-arrow { width: 36px; height: 36px; border-radius: 11px; border: 1px solid rgba(255,255,255,0.14); background: rgba(255,255,255,0.06); color: #c3cfe4; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.16s, color 0.16s, border-color 0.16s, transform 0.16s; }
.e2-arrow:hover { background: rgba(255,255,255,0.16); color: #fff; border-color: rgba(255,255,255,0.28); transform: translateY(-1px); }
.e2-arrow:active { transform: scale(0.94); }
.e2-arrow:focus-visible { outline: 2px solid #cfe0ff; outline-offset: 2px; }

/* HERO-витрина: живой mesh, вращающийся обод, водяной знак, стеклянные чипы. */
.e2-hero { position: relative; min-height: 150px; border-radius: 16px; overflow: hidden; margin-bottom: 14px; background: linear-gradient(140deg, #24406b, #2b3a73); background: linear-gradient(140deg, color-mix(in srgb, var(--e2-from) 26%, #0b1428), color-mix(in srgb, var(--e2-to) 22%, #0b1428)); isolation: isolate; }
.e2-hero-mesh { position: absolute; inset: -45%; z-index: -1; filter: blur(26px); opacity: 0.6; background: radial-gradient(circle at 28% 30%, var(--e2-from), transparent 46%), radial-gradient(circle at 72% 66%, var(--e2-to), transparent 46%), conic-gradient(from 0deg at 50% 50%, var(--e2-from), var(--e2-to), var(--e2-from)); animation: e2-spin 16s linear infinite; }
.e2-hero-ring { position: absolute; inset: 0; border-radius: 16px; padding: 1.4px; background: conic-gradient(from 0deg, transparent, var(--e2-from), var(--e2-to), transparent 70%); -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite: xor; mask-composite: exclude; opacity: 0.8; animation: e2-spin 9s linear infinite; }
.e2-hero-glyph { position: absolute; right: -16px; bottom: -26px; color: #fff; opacity: 0.12; z-index: -1; animation: e2-bob 6s ease-in-out infinite; }
.e2-hero-body { position: relative; height: 100%; display: flex; flex-direction: column; justify-content: flex-end; gap: 10px; padding: 18px; }
.e2-hero-kick { font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase; color: rgba(255,255,255,0.72); font-weight: 700; }
.e2-hero-name { font-size: 25px; font-weight: 750; letter-spacing: -0.02em; color: #fff; text-shadow: 0 2px 18px rgba(0,0,0,0.35); }
.e2-hero-chips { display: flex; flex-wrap: wrap; gap: 7px; }
.e2-chip { font-size: 11.5px; font-weight: 600; color: #eaf1ff; padding: 5px 11px; border-radius: 999px; background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.22); backdrop-filter: blur(4px); white-space: nowrap; animation: e2-chip 0.5s ease both; }
.e2-chip:nth-child(2) { animation-delay: 0.08s; }
.e2-chip:nth-child(3) { animation-delay: 0.16s; }

.e2-dotsrow { display: flex; align-items: center; gap: 7px; padding: 0 2px 14px; }
.e2-dotbtn { height: 6px; border-radius: 999px; border: none; padding: 0; cursor: pointer; background: rgba(255,255,255,0.2); width: 22px; transition: background 0.3s, width 0.3s; }
.e2-dotbtn--on { width: 40px; background: linear-gradient(92deg, var(--e2-from), var(--e2-to)); }
.e2-dotbtn:focus-visible { outline: 2px solid #cfe0ff; outline-offset: 3px; }

.e2-tiles { list-style: none; margin: 0; padding: 0; display: grid; gap: 9px; perspective: 900px; }
.e2-tiles--two { grid-template-columns: 1fr 1fr; }
.e2-sl-enter-active, .e2-sl-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.e2-sl-enter-from { opacity: 0; transform: translateX(14px); }
.e2-sl-leave-to { opacity: 0; transform: translateX(-14px); }

.e2-tile { position: relative; display: flex; gap: 12px; align-items: center; padding: 13px 15px; border-radius: 14px; background: rgba(255, 255, 255, 0.04); background: color-mix(in srgb, var(--hue) 8%, rgba(255,255,255,0.015)); border: 1px solid rgba(255, 255, 255, 0.1); border: 1px solid color-mix(in srgb, var(--hue) 16%, transparent); transform-style: preserve-3d; transition: transform 0.2s ease, border-color 0.18s, background 0.18s, box-shadow 0.18s; animation: e2-tilein 0.5s cubic-bezier(.2,.85,.3,1) both; animation-delay: var(--d); }
.e2-tile:hover { border-color: color-mix(in srgb, var(--hue) 50%, transparent); background: color-mix(in srgb, var(--hue) 14%, rgba(255,255,255,0.02)); box-shadow: 0 14px 34px color-mix(in srgb, var(--hue) 26%, transparent); }
.e2-tile-ic { flex: none; width: 34px; height: 34px; border-radius: 11px; display: flex; align-items: center; justify-content: center; background: rgba(255, 255, 255, 0.1); background: color-mix(in srgb, var(--hue) 22%, transparent); color: var(--hue); transition: transform 0.2s; }
.e2-tile:hover .e2-tile-ic { transform: translateZ(14px) scale(1.08); }
.e2-tile-txt { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.e2-tile-t { font-size: 14px; font-weight: 650; color: #f1f5fb; }
.e2-tile-d { font-size: 12.6px; line-height: 1.4; color: #8b9cba; }

@keyframes e2-rise { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
@keyframes e2-titlein { from { opacity: 0; transform: translateY(18px); filter: blur(10px); } to { opacity: 1; transform: none; filter: blur(0); } }
@keyframes e2-panelin { from { opacity: 0; transform: translateY(22px) scale(0.975); } to { opacity: 1; transform: none; } }
@keyframes e2-tilein { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes e2-chip { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes e2-drift-a { 0% { transform: translate(0,0) scale(1); } 50% { transform: translate(7%,5%) scale(1.16); } 100% { transform: translate(0,0) scale(1); } }
@keyframes e2-drift-b { 0% { transform: translate(0,0) scale(1); } 50% { transform: translate(-6%,-4%) scale(1.13); } 100% { transform: translate(0,0) scale(1); } }
@keyframes e2-spin { to { transform: rotate(360deg); } }
@keyframes e2-shimmer { to { background-position: 220% center; } }
@keyframes e2-halo { 0%, 100% { opacity: 0.32; transform: scale(0.98); } 50% { opacity: 0.68; transform: scale(1.05); } }
@keyframes e2-shine { 0% { transform: translateX(-160%) skewX(-18deg); } 55%, 100% { transform: translateX(320%) skewX(-18deg); } }
@keyframes e2-sheen { 0% { transform: translateX(-160%) skewX(-16deg); } 45%, 100% { transform: translateX(320%) skewX(-16deg); } }
@keyframes e2-float { 0% { transform: translateY(0); opacity: 0; } 12% { opacity: 0.75; } 88% { opacity: 0.45; } 100% { transform: translateY(-380px); opacity: 0; } }
@keyframes e2-logo { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-3px) rotate(2deg); } }
@keyframes e2-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }

/* Слабое железо и вкус к покою: глушим петли, оставляем мгновенные состояния. */
@media (prefers-reduced-motion: reduce) {
  .e2-root, .e2-root * { animation: none !important; }
  .e2-sparks, .e2-hero-mesh, .e2-hero-ring, .e2-spot { display: none; }
}

@media (max-width: 1000px) {
  .e2-grid { grid-template-columns: 1fr; gap: 26px; padding: 36px 20px 30px; max-width: 620px; }
  .e2-title { font-size: clamp(27px, 6.4vw, 36px); }
  .e2-lead, .e2-move { max-width: none; }
  .e2-tiles--two { grid-template-columns: 1fr; }
  .e2-actions { gap: 14px; }
  .e2-cta { width: 100%; justify-content: center; }
  .e2-demo { width: 100%; justify-content: center; }
}

/* Полоса на панели приложения: тот же тёмный язык, но тихо и в одну строку. */
.e2-strip { position: relative; display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 24px; padding: 12px 14px; border-radius: 16px; border: 1px solid rgba(120, 150, 255, 0.22); background: linear-gradient(122deg, #101f3f 0%, #0b1430 60%, #0a1026 100%); color: #eef2f8; font-family: var(--w-font, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif); box-shadow: 0 10px 26px rgba(6, 11, 25, 0.18); }
.e2-strip-mark { display: inline-flex; flex: none; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 11px; background: rgba(255, 255, 255, 0.06); }
.e2-strip-mark img { display: block; border-radius: 8px; }
.e2-strip-txt { flex: 1 1 240px; min-width: 0; }
.e2-strip-txt b { display: block; font-size: 13.5px; font-weight: 650; letter-spacing: -0.01em; }
.e2-strip-txt i { display: block; margin-top: 2px; font-style: normal; font-size: 12.5px; color: #a3b3d0; }
.e2-strip-cta { flex: none; min-height: 40px; padding: 9px 18px; border: none; border-radius: 10px; background: linear-gradient(96deg, #6a7bff, #4f8bff); color: #fff; font: inherit; font-size: 13.5px; font-weight: 650; cursor: pointer; box-shadow: 0 8px 20px rgba(79, 139, 255, 0.28); }
.e2-strip-cta:hover { filter: brightness(1.08); }
.e2-strip-cta:focus-visible { outline: 2px solid #cfe0ff; outline-offset: 2px; }
.e2-strip-close { flex: none; display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; margin-left: -6px; border: none; border-radius: 10px; background: transparent; color: #8fa0c2; cursor: pointer; }
.e2-strip-close:hover { background: rgba(255, 255, 255, 0.08); color: #eef2f8; }
.e2-strip-close:focus-visible { outline: 2px solid #cfe0ff; outline-offset: 2px; }
@media (max-width: 619px) {
  .e2-strip-cta { width: 100%; min-height: 44px; }
  /* Кнопка на всю ширину уходит на свою строку, крестик садится в угол полосы. */
  .e2-strip-close { position: absolute; top: 4px; right: 4px; width: 44px; height: 44px; margin-left: 0; }
  .e2-strip-txt { padding-right: 36px; }
}
</style>

